import { FC, useEffect, useState, type ChangeEvent } from "react";
import { useAppStore } from "store/store";
import Content, { ContentNS } from "content/content";
import { useSearchParams } from "react-router-dom";
import { normalizeName } from "utils";

/* models */
import Pax from "models/pax";
import { DocProcessingStatus } from "models/doc-processing-status";
import { DocumentRequirement } from "models/checklist/checklist-item/checklist-item";
import AllowedDocument from "models/documents/allowed-document";
import { ItemLevelNavigationError } from "models/navigation/item-navigation-error";
import ChecklistResolutionStatus from "models/checklist/checklist-resolution-status";
import { PaxCheckDocumentSidesConfiguration } from "api";

/* hooks */
import useAllowedDocumentsForTravelDoc from "features/document-capture/hooks/useAllowedDocumentsForTravelDoc";
import usePaxSession from "features/shared/hooks/usePaxSession";
import useNavigateWithQuery from "features/shared/hooks/navigation/useNavigateWithQuery";
import useUploadSingleSideDocument from "features/shared/hooks/useUploadSingleSideDocument";
import useScrollToPageTitle from "features/document-page/hooks/useScrollToPageTitle";
import useItemLevelNavigationState, {
  ItemNavigationState,
  ItemTypeNavigationState,
} from "features/shared/hooks/navigation/useItemLevelNavigationState";

/* components */
import Checkbox from "features/shared/components/checkbox/Checkbox";
import DocumentSelector from "./components/document-selector/DocumentSelector";
import SelectFileButton from "features/shared/components/select-file-button/SelectFileButton";

/* styles */
import ToolsButtons from "features/document-page/styles/ToolsButtons.styled";
import PrimaryButton from "features/shared/components/buttons/PrimaryButton.styled";
import Body from "features/shared/styles/typography/Body.styled";
import { Summary } from "features/shared/styles/typography/Summary.styled";
import StyledItemResolver from "./styles/StyledItemResolver.styled";
import { MicroHeader } from "features/shared/styles/typography/MicroHeader.styled";
import SelectPassengerApplyBox from "../basic-document/styles/SelectPassengerApplyBox.styled";
import SelectPassengerApply from "../basic-document/styles/SelectPassengerApply.styled";
// @ts-expect-error - This is how we utilize dynamic imports with WebPack NormalModuleReplacementPlugin
// See configuration in webpack.config.js
import ToolsToResolve from "./styles/ToolsToResolve-APP_CLIENT.styled";
import ResolverTextGroup from "./styles/ResolverTextGroup.styled";

interface ResolverByItemProps {
  item: DocumentRequirement;
}

interface ResolverByTypeProps {
  itemType: string;
}

interface ResolverContentProps {
  itemType: string;
  itemTitle: string;
  shortTitle: string;
  allowedDocuments: AllowedDocument[];
}

const textDocs = (s: string, args: any = {}) =>
  Content.shared.t(s, { ns: ContentNS.docs, ...args });

// with few options on the page the selected document is obvious, so clients may
// shorten the action buttons by defining "scan-short" / "upload-short"
const LONG_ACTION_CAPTION_MIN_OPTIONS = 5;

const actionCaption = (
  action: "scan" | "upload",
  docTitle: string,
  optionsCount: number
): string => {
  const caption = `${textDocs(action, { docTitle })}`;

  if (optionsCount >= LONG_ACTION_CAPTION_MIN_OPTIONS) {
    return caption;
  }

  return `${textDocs(`${action}-short`, { docTitle, defaultValue: caption })}`;
};

const UploadButton: FC<{
  selectedDocument: AllowedDocument;
  optionsCount: number;
  onFileSelectMultiSided: (e: ChangeEvent<HTMLInputElement>) => void;
  onFileSelectSingleSided: (e: ChangeEvent<HTMLInputElement>) => void;
}> = ({
  selectedDocument,
  optionsCount,
  onFileSelectMultiSided,
  onFileSelectSingleSided,
}) => {
  const caption = actionCaption(
    "upload",
    selectedDocument.title,
    optionsCount
  );

  if (selectedDocument.isMultiSideExpected()) {
    return (
      <SelectFileButton
        onChange={onFileSelectMultiSided}
        label={caption}
        primary={!selectedDocument.isScanPossible()}
      />
    );
  }

  return (
    <SelectFileButton
      primary={!selectedDocument.isScanPossible()}
      onChange={onFileSelectSingleSided}
      label={caption}
    />
  );
};

const ResolverContent: FC<ResolverContentProps> = ({
  itemType,
  itemTitle,
  shortTitle,
  allowedDocuments,
}) => {
  const navigate = useNavigateWithQuery();

  const sessionId = useAppStore((state) => state.sessionId);

  const paxSession = usePaxSession();
  if (!paxSession) {
    throw new Error("invalid paxSession");
  }

  const paxSessions = useAppStore((state) => state.paxSessions);

  const [searchParams, setSearchParams] = useSearchParams();

  const itemLevelNavigationState = useItemLevelNavigationState(searchParams);

  const processedDocTypes = useAppStore((state) => state.processedDocTypes);

  const eligiblePaxSessionsForMultipaxApply = paxSessions.filter(
    (otherPaxSession) => {
      return (
        paxSession.paxSessionId !== otherPaxSession.paxSessionId &&
        otherPaxSession.checklist.resolutionStatus !==
          ChecklistResolutionStatus.ReadyToFly
      );
    }
  );

  const [additionalPaxSessionIds, setAdditionalPaxSessionIds] = useState<
    string[]
  >([]);

  useScrollToPageTitle();

  if (allowedDocuments.length === 0) {
    // this should not be the case, as either taken from item,
    // or from allowed travel docs from session — unless backend
    // returns such a broken session (eg loophole in rules)
    throw new ItemLevelNavigationError(
      sessionId,
      paxSession?.paxSessionId,
      `somehow no allowed documents for resolver`
    );
  }

  // define defaultDocumentType:
  // first from processing or first from allowed documents
  const firstDocTypeFromProcessing =
    processedDocTypes && processedDocTypes.length > 0
      ? processedDocTypes[0]
      : undefined;

  const defaultDocumentType =
    firstDocTypeFromProcessing || allowedDocuments[0].type;

  useEffect(() => {
    if (itemLevelNavigationState.forceDocumentType === undefined) {
      // forceDocumentType is not in search params,
      // need to select default document and set it to search params to continue
      searchParams.set("forceDocumentType", defaultDocumentType);
      setSearchParams(searchParams);
    }
  }, [
    itemLevelNavigationState,
    defaultDocumentType,
    searchParams,
    setSearchParams,
  ]);

  /*
  selected document - read from search params (forceDocumentType), as ItemResolver effectively
  selects forceDocumentType for Capture/Upload, which now take it directly from
  search params (via useItemLevelNavigationState), hence when user selects
  different document in ItemResolver, we should change forceDocumentType in search params.
  */
  const selectDocumentType = (documentType: string) => {
    // change forceDocumentType to documentType
    searchParams.set("forceDocumentType", documentType);
    setSearchParams(searchParams);
  };

  const [onFileSelectSingleSided] = useUploadSingleSideDocument(
    allowedDocuments.some((doc) => doc.expectLongProcessing === true),
    additionalPaxSessionIds,
    allowedDocuments.length === 1
      ? allowedDocuments[0].pageIndexToProcess ?? undefined
      : undefined
  );

  const setProcessingStatus = useAppStore((state) => state.setProcessingStatus);
  const setProcessingMayTakeLong = useAppStore(
    (state) => state.setProcessingMayTakeLong
  );

  const onFileSelectMultiSided = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!(e.target.files && e.target.files.length > 0)) {
      return;
    }

    navigate("../upload", {
      state: {
        itemType: itemType,
        processingMayTakeLong: allowedDocuments.some(
          (doc) => doc.expectLongProcessing === true
        ),
        inputFile: e.target.files[0],
        additionalPaxSessionIds: additionalPaxSessionIds,
        pageIndexToProcess:
          allowedDocuments.length === 1
            ? allowedDocuments[0].pageIndexToProcess ?? undefined
            : undefined,
      },
    });
  };

  const onScanClick = () => {
    setProcessingStatus(DocProcessingStatus.None, null);
    setProcessingMayTakeLong(null);
    navigate("../capture");
  };

  // try to find selected document from allowed docs by forceDocumentType
  const selectedDocument = itemLevelNavigationState.forceDocumentType
    ? allowedDocuments.find(
        (doc) => doc.type === itemLevelNavigationState.forceDocumentType
      )
    : undefined;

  if (selectedDocument === undefined) {
    return null;
  }

  // Opt-in, per-client instruction line: only shown when docs.json has the
  // matching key
  const instructionKey =
    selectedDocument.sidesConfiguration ===
    PaxCheckDocumentSidesConfiguration.MULTIPLE_SIDES_OR_FILES
      ? "scan-instruction-multi-side"
      : "scan-instruction-single-side";

  const instruction = Content.shared.instance.exists(instructionKey, {
    ns: ContentNS.docs,
  })
    ? `${textDocs(instructionKey)}`
    : null;

  const getPaxPrintName = (pax: Pax): string => {
    return normalizeName(pax.firstName + " " + pax.surname);
  };

  const handlePaxToggle = (paxSessionId: string) => (value: boolean) => {
    setAdditionalPaxSessionIds((prevState) => {
      if (value) {
        // Add paxSessionId if value is true and it's not already in the array
        return prevState.includes(paxSessionId)
          ? prevState
          : [...prevState, paxSessionId];
      } else {
        // Remove paxSessionId if value is false
        return prevState.filter((id) => id !== paxSessionId);
      }
    });
  };

  return (
    <StyledItemResolver>
      <ToolsToResolve>
        <ResolverTextGroup>
          <Summary>{`${textDocs("item-subtitle", {
            itemTitle: itemTitle,
          })}`}</Summary>
          <Body>{`${textDocs("item-resolver-subtitle", {
            itemTitle: shortTitle,
          })}`}</Body>
          {instruction ? <Body>{instruction}</Body> : null}
        </ResolverTextGroup>
        <DocumentSelector
          allowedDocuments={allowedDocuments}
          selectedDocument={selectedDocument}
          onSelect={selectDocumentType}
        />
        {selectedDocument.multipaxResolutionSupported &&
        eligiblePaxSessionsForMultipaxApply.length > 0 ? (
          <div>
            <MicroHeader>{`${textDocs(
              "multipax-apply-suggestion"
            )}`}</MicroHeader>
            <SelectPassengerApplyBox>
              {eligiblePaxSessionsForMultipaxApply.map((otherPaxSession, i) => {
                return (
                  <SelectPassengerApply key={`passenger-${i}`}>
                    <Checkbox
                      label={
                        <Body>{getPaxPrintName(otherPaxSession.pax)}</Body>
                      }
                      checked={additionalPaxSessionIds.includes(
                        otherPaxSession.paxSessionId
                      )}
                      onChange={handlePaxToggle(otherPaxSession.paxSessionId)}
                      error={null}
                    />
                  </SelectPassengerApply>
                );
              })}
            </SelectPassengerApplyBox>
          </div>
        ) : null}
        <ToolsButtons>
          {selectedDocument.isScanPossible() ? (
            <PrimaryButton onClick={onScanClick}>
              {actionCaption(
                "scan",
                selectedDocument.title,
                allowedDocuments.length
              )}
            </PrimaryButton>
          ) : null}
          <UploadButton
            selectedDocument={selectedDocument}
            optionsCount={allowedDocuments.length}
            onFileSelectMultiSided={onFileSelectMultiSided}
            onFileSelectSingleSided={onFileSelectSingleSided}
          />
        </ToolsButtons>
      </ToolsToResolve>
    </StyledItemResolver>
  );
};

const ResolverByItem: FC<ResolverByItemProps> = ({ item }) => {
  const allowedDocuments = item.allowedDocumentsSet.allowedDocuments;

  const itemTitle = item.title;

  const shortTitle = item.shortTitle ? item.shortTitle : itemTitle;

  return (
    <ResolverContent
      itemType={item.itemType}
      itemTitle={itemTitle}
      shortTitle={shortTitle}
      allowedDocuments={allowedDocuments}
    />
  );
};

const ResolverByType: FC<ResolverByTypeProps> = ({ itemType }) => {
  const textDocs = (s: string) => Content.shared.t(s, { ns: ContentNS.docs });

  // as only TRAVEL_DOC is allowed atm, get all allowed
  // travel documents from session
  const allowedDocuments = useAllowedDocumentsForTravelDoc();

  const itemTitle = textDocs("itemTitle.TRAVEL_DOC");

  return (
    <ResolverContent
      itemType={itemType}
      itemTitle={itemTitle}
      shortTitle={itemTitle}
      allowedDocuments={allowedDocuments}
    />
  );
};

const ItemResolverWithOptions: FC = () => {
  useAppStore((state) => state.locale);

  const [searchParams] = useSearchParams();

  const itemLevelNavigationState = useItemLevelNavigationState(searchParams);

  const sessionId = useAppStore((state) => state.sessionId);

  const paxSession = usePaxSession();

  if (itemLevelNavigationState instanceof ItemNavigationState) {
    // itemId
    const item = itemLevelNavigationState.item;
    if (item instanceof DocumentRequirement === false) {
      throw new ItemLevelNavigationError(
        sessionId,
        paxSession?.paxSessionId,
        `Item is not DocumentRequirement: ${item.id}`
      );
    }

    return <ResolverByItem item={item as DocumentRequirement} />;
  }

  if (itemLevelNavigationState instanceof ItemTypeNavigationState) {
    // itemType
    return <ResolverByType itemType={itemLevelNavigationState.itemType} />;
  }

  // neither itemId or itemType - should not be the case
  throw new ItemLevelNavigationError(
    sessionId,
    paxSession?.paxSessionId,
    `neither itemId or itemType navigation state, which is odd`
  );
};

export default ItemResolverWithOptions;
